import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { IAppState } from 'infra/AppState'
import { getBusinessPrefs, subscribeBusinessPrefs, updateBusinessPrefs } from './businessPrefs'

/**
 * "New feature" tags on the sidebar (Collection, Settings) and on the UPI QR row
 * inside Settings: a pulse ring plus a NEW pill. One shared look (components/NewTag); the rules live here.
 *
 * These features shipped on Android first. The tags exist to tell every merchant
 * that the same features are now on WEB. The flags are per business in
 * localStorage (utils/businessPrefs), survive logout, and a different account
 * gets its own set. No timers.
 *
 *  - Collection: the pulse ring stops on the first click of the tab; the NEW
 *    pill stays until a bulk reminder (sample, free or paid) is sent from web.
 *  - Settings: every merchant sees the NEW pill and the pulse ring, whether or
 *    not they already have a UPI QR (a new web user must still learn the feature
 *    is here). Both go away on the first click of the Settings tab — by then the
 *    merchant has seen what is new.
 *  - UPI QR row inside Settings: its NEW pill points at the new row once Settings
 *    is open, and goes away the first time that row is clicked.
 */

// Android's HasTriedBulkReminder (any campaign, any platform): routes past the
// intro carousel and feeds the sidebar icon's `paid_status`. Not a tag input.
export const markBulkReminderTried = (businessId: string) => {
  if (!businessId || getBusinessPrefs(businessId).bwr.hasTriedCampaign) {
    return
  }
  updateBusinessPrefs(businessId, { bwr: { hasTriedCampaign: true } })
}

// Called after a successful sample / free / paid send from web
export const markBulkReminderSentOnWeb = (businessId: string) => {
  if (!businessId || getBusinessPrefs(businessId).featureTags.bulkReminderSentOnWeb) {
    return
  }
  updateBusinessPrefs(businessId, { featureTags: { bulkReminderSentOnWeb: true } })
}

export interface IFeatureTags {
  collection: { pulse: boolean; isNew: boolean; dismissPulse: () => void }
  settings: { pulse: boolean; isNew: boolean; dismissPulse: () => void }
  upiQrRow: { isNew: boolean; dismiss: () => void }
}

export const useFeatureTags = (): IFeatureTags => {
  const businessId = useSelector((app: IAppState) => app.Dashboard.merchantProfile?.data?.id || '')
  const [, setVersion] = useState(0)

  useEffect(() => {
    const listener = () => setVersion((version) => version + 1)
    return subscribeBusinessPrefs(listener)
  }, [])

  const prefs = getBusinessPrefs(businessId)
  const enabled = !!businessId

  const dismissCollectionPulse = useCallback(
    () => updateBusinessPrefs(businessId, { bwr: { tabPulseSeen: true } }),
    [businessId]
  )
  const dismissUpiQrRow = useCallback(
    () => updateBusinessPrefs(businessId, { featureTags: { upiQrRowClicked: true } }),
    [businessId]
  )
  const dismissSettingsPulse = useCallback(
    () => updateBusinessPrefs(businessId, { featureTags: { settingsPulseSeen: true } }),
    [businessId]
  )

  const collectionNew = enabled && !prefs.featureTags.bulkReminderSentOnWeb
  const settingsNew = enabled && !prefs.featureTags.settingsPulseSeen
  const upiRowNew = enabled && !prefs.featureTags.upiQrRowClicked

  return {
    collection: {
      isNew: collectionNew,
      pulse: collectionNew && !prefs.bwr.tabPulseSeen,
      dismissPulse: dismissCollectionPulse
    },
    settings: {
      isNew: settingsNew,
      pulse: settingsNew,
      dismissPulse: dismissSettingsPulse
    },
    upiQrRow: { isNew: upiRowNew, dismiss: dismissUpiQrRow }
  }
}
